import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

interface CompanyStats {
  id: string;
  name: string;
  slug: string;
  email: string;
  subscription_status: string;
  subscription_plan_id: number;
  plan_name: string;
  price_monthly: number;
  total_users: number;
  total_wheels: number;
  phone: string;
  total_products: number;
  total_spins: number;
  created_at: string;
}

interface CompaniesResponse {
  success: boolean;
  data: {
    companies: CompanyStats[];
    total: number;
    page: number;
    totalPages: number;
  };
}

interface Plan {
  id: number;
  name: string;
  slug: string;
  price_monthly: number;
}

const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyStats[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');


  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get<CompaniesResponse>(`/admin/companies?${params}`);

      if (response.data.success) {
        setCompanies(response.data.data.companies);
        setTotal(response.data.data.total);
        setTotalPages(response.data.data.totalPages);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  const loadPlans = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: Plan[] }>('/admin/plans');
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [page, search, statusFilter, loadCompanies]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);


  const handleStatusChange = async (companyId: string, newStatus: string) => {
    if (!window.confirm(`Tem certeza que deseja alterar o status para ${newStatus}?`)) {
      return;
    }

    try {
      await api.patch(`/admin/companies/${companyId}/status`, { status: newStatus });
      loadCompanies();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    }
  };

  const handlePlanChange = async (companyId: string, planId: string) => {
    const plan = plans.find(p => p.id === parseInt(planId));
    if (!plan) return;

    if (!window.confirm(`Tem certeza que deseja alterar o plano para ${plan.name}?`)) {
      return;
    }

    try {
      await api.patch(`/admin/companies/${companyId}/plan`, { plan_id: parseInt(planId) });
      loadCompanies();
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      alert('Erro ao atualizar plano');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      suspended: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatWhatsApp = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');

    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  };

  const openWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    window.open(`https://wa.me/+55${cleaned}`);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Empresas Cadastradas</h1>
        <p className="text-gray-600">Total: {total} empresas</p>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Buscar por nome, email ou slug..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="trial">Trial</option>
          <option value="cancelled">Cancelado</option>
          <option value="suspended">Suspenso</option>
        </select>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Whatsapp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuários
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Roletas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Leads
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alterar Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alterar Plano
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{company.name}</div>
                          <div className="text-sm text-gray-500">{company.slug}</div>
                          <div className="text-xs text-gray-400">{company.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex align-center gap-2">
                          {formatWhatsApp(company.phone)}
                          <button onClick={() => openWhatsApp(company.phone)} className="cursor-pointer w-6 h-6">
                            <img src="https://img.icons8.com/color/48/000000/whatsapp.png" alt="WhatsApp" className='w-6 h-6' />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{company.plan_name || 'N/A'}</div>
                        {company.price_monthly && (
                          <div className="text-xs text-gray-500">R$ {company.price_monthly}/mês</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(company.subscription_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.total_users}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.total_wheels}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.total_spins}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(company.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <select
                          onChange={(e) => handleStatusChange(company.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                          defaultValue=""
                        >
                          <option value="" disabled>Alterar status</option>
                          <option value="active">Ativo</option>
                          <option value="trial">Trial</option>
                          <option value="cancelled">Cancelado</option>
                          <option value="suspended">Suspenso</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <select
                          onChange={(e) => handlePlanChange(company.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                          value={company.subscription_plan_id || ''}
                        >
                          <option value="" disabled>Selecione o plano</option>
                          {plans.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} - R$ {plan.price_monthly}/mês
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>

              <span className="px-4 py-2 text-gray-700">
                Página {page} de {totalPages}
              </span>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompaniesPage;
